<template>
  <ion-page>
    <ion-header />
    <ion-content :scroll-events="enableScrollEvent" @ionScroll="onScroll">
      <slot></slot>
    </ion-content>
    <ion-footer class="ion-no-border" v-if="showFooter"> </ion-footer>
  </ion-page>
</template>

<script>
import { IonPage, IonHeader, IonContent, IonFooter, IonToolbar } from '@ionic/vue';
import { isIosNativeOnly } from '@/utils/platform-check';

export default {
  name: 'AbsolutelyLayout',
  components: {
    IonPage,
    IonHeader,
    IonContent,
    IonFooter,
    IonToolbar,
  },
  props: {
    showFooter: {
      type: Boolean,
      default: false,
    },
    enableScrollEvent: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    isIos() {
      return isIosNativeOnly();
    },
    async hideScrollbar() {
      // Hide scrollbar programmatically for native apps
      try {
        const ionContent = this.$el?.querySelector('ion-content');
        if (ionContent && typeof ionContent.getScrollElement === 'function') {
          const scrollElement = await ionContent.getScrollElement();
          if (scrollElement) {
            // Hide scrollbar for all browsers and native WebViews
            scrollElement.style.scrollbarWidth = 'none'; // Firefox
            scrollElement.style.msOverflowStyle = 'none'; // IE and Edge

            // Disable bounce/overscroll effect
            scrollElement.style.overscrollBehavior = 'none';
            scrollElement.style.webkitOverflowScrolling = 'touch';

            // Create a unique ID for this scroll element
            const scrollId = scrollElement.id || `scroll-${Date.now()}`;
            if (!scrollElement.id) {
              scrollElement.id = scrollId;
            }

            // Inject global style to hide scrollbar and disable bounce for this specific element
            const styleId = `hide-scrollbar-${scrollId}`;
            if (!document.getElementById(styleId)) {
              const style = document.createElement('style');
              style.id = styleId;
              style.textContent = `
                #${scrollId}::-webkit-scrollbar,
                #${scrollId} *::-webkit-scrollbar {
                  display: none !important;
                  width: 0 !important;
                  height: 0 !important;
                  background: transparent !important;
                  -webkit-appearance: none !important;
                }
                #${scrollId} {
                  overscroll-behavior: none !important;
                  -webkit-overflow-scrolling: touch !important;
                }
              `;
              document.head.appendChild(style);
            }
          }
        }
      } catch (error) {
        console.warn('[AbsolutelyLayout] Error hiding scrollbar:', error);
      }
    },
    onScroll(event) {
      this.$emit('on-scroll', event);
    },
  },
  async mounted() {
    // Hide scrollbar programmatically for native apps
    await this.$nextTick();
    await this.hideScrollbar();
    // Also try after a short delay to ensure ion-content is fully initialized
    setTimeout(() => {
      this.hideScrollbar();
    }, 100);
    // Try again after longer delay for native apps
    setTimeout(() => {
      this.hideScrollbar();
    }, 500);
  },
  async ionViewDidEnter() {
    // Hide scrollbar when page becomes visible (for native apps)
    await this.$nextTick();
    await this.hideScrollbar();
    setTimeout(() => {
      this.hideScrollbar();
    }, 100);
  },
};
</script>

<style scoped>
/* Disable text/element selection on all absolutely pages */
* {
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  -webkit-touch-callout: none;
  -webkit-tap-highlight-color: transparent;
}

/* Allow selection for input fields and textareas */
input,
textarea {
  -webkit-user-select: text;
  -moz-user-select: text;
  -ms-user-select: text;
  user-select: text;
}

ion-toolbar {
  --background: transparent !important;
}

.footer-md::before {
  display: none;
}
</style>
